import React, { useState, useEffect } from 'react';
import { Lock, X } from 'lucide-react';
import config from '../config';

function PagarCheckout({ token }) {
  const [pago, setPago] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirigiendo, setRedirigiendo] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || config.API_URL;
  const buildEndpoint = (path) => {
    const rawBase = (apiUrl || '').trim().replace(/\/+$/, '');
    const apiBase = rawBase.endsWith('/api') ? rawBase : `${rawBase}/api`;
    return rawBase ? `${apiBase}${path}` : `/api${path}`;
  };

  useEffect(() => {
    fetch(buildEndpoint(`/pagos/checkout/${token}`))
      .then(r => r.json())
      .then(data => {
        if (data.success) setPago(data.pago);
        else setError(data.error || 'Link de pago no encontrado o expirado');
      })
      .catch(() => setError('Error al cargar los datos del pago'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handlePagar = async () => {
    if (redirigiendo) return;
    setRedirigiendo(true);
    setError(null);
    try {
      const res = await fetch(buildEndpoint(`/pagos/iniciar/${token}`));
      const data = await res.json();
      if (data.success && data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        setError(data.error || 'No se pudo iniciar el pago. Intenta de nuevo.');
        setRedirigiendo(false);
      }
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
      setRedirigiendo(false);
    }
  };

  const montoFormateado = pago
    ? Number(pago.monto).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.spinner} />
          <p style={s.centered}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (error && !pago) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logoRow}>
            <span style={s.logoText}>Activo Contadores</span>
          </div>
          <div style={s.errorIcon}><X size={28} aria-hidden="true" /></div>
          <h2 style={{ ...s.title, textAlign: 'center' }}>Link no válido</h2>
          <p style={s.errorMsg}>{error}</p>
          <div style={s.contacto}>
            ¿Necesitas ayuda? Contáctanos:<br />
            <a href="mailto:agustin@activocontadores.com" style={s.contactoLink}>agustin@activocontadores.com</a>
            &nbsp;·&nbsp;664 494 0143
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>

        {/* Header */}
        <div style={s.logoRow}>
          <span style={s.logoText}>Activo Contadores</span>
        </div>

        {/* Monto y detalle */}
        <div style={s.amountBox}>
          <span style={s.amountLabel}>Total a pagar</span>
          <span style={s.amountValue}>${montoFormateado} <span style={{ fontSize: '18px', fontWeight: '600' }}>MXN</span></span>
          {pago?.concepto && <span style={s.concepto}>{pago.concepto}</span>}
          {pago?.nombre_cliente && <span style={s.cliente}>Para: {pago.nombre_cliente}</span>}
          {pago?.expiracion_en && (
            <span style={{ fontSize: '12px', color: '#92400e', marginTop: '6px', background: '#fef3c7', borderRadius: '4px', padding: '3px 8px' }}>
              Válido hasta: {new Date(pago.expiracion_en).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>

        {/* Info de seguridad */}
        <div style={s.secureBox}>
          <span style={s.secureLock}><Lock size={14} aria-hidden="true" /></span>
          <span style={s.secureText}>Pago seguro procesado por Openpay · Cifrado SSL 256 bits</span>
        </div>

        {error && <p style={s.errorMsg}>{error}</p>}

        {/* Botón principal */}
        <button
          type="button"
          onClick={handlePagar}
          disabled={redirigiendo}
          style={{ ...s.btn, opacity: redirigiendo ? 0.7 : 1 }}
        >
          {redirigiendo ? 'Redirigiendo...' : `Pagar $${montoFormateado} MXN`}
        </button>

        {redirigiendo && (
          <p style={s.redirigendoMsg}>
            Serás redirigido a la página segura de pago de Openpay...
          </p>
        )}

        {/* Marcas de tarjetas */}
        <div style={s.brandsRow}>
          <img src="/card-visa.png" alt="Visa" style={s.brandImg} />
          <img src="/card-mastercard.png" alt="Mastercard" style={s.brandImg} />
          <img src="/card-amex.png" alt="American Express" style={s.brandImg} />
          <img src="/card-carnet.png" alt="Carnet" style={s.brandImg} />
        </div>

        {/* Logo Openpay oficial */}
        <div style={s.openpayRow}>
          <span style={s.openpayLabel}>Powered by</span>
          <img
            src="/openpay-logo.jpg"
            alt="Openpay"
            style={s.openpayLogo}
          />
        </div>

        {/* Contacto */}
        <div style={s.contacto}>
          ¿Dudas sobre tu pago? Contáctanos:<br />
          <a href="mailto:agustin@activocontadores.com" style={s.contactoLink}>agustin@activocontadores.com</a>
          &nbsp;·&nbsp;664 494 0143<br />
          Calle Ryerson 106, Zona Centro, Ensenada, B.C. C.P. 22800
        </div>

        {/* Links legales */}
        <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '11px', color: '#9ca3af' }}>
          <a href="/privacidad" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'underline' }}>Política de Privacidad</a>
          &nbsp;·&nbsp;
          <a href="/terminos" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'underline' }}>Términos y Condiciones</a>
        </div>

      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '36px 32px',
    maxWidth: '440px',
    width: '100%',
    boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch'
  },
  logoRow: { display: 'flex', alignItems: 'center', marginBottom: '24px' },
  logoText: { color: '#16a34a', fontWeight: '700', fontSize: '18px', letterSpacing: '-0.3px' },
  amountBox: {
    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
    border: '1px solid #86efac',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  amountLabel: { color: '#15803d', fontSize: '13px', fontWeight: '500' },
  amountValue: { fontSize: '32px', fontWeight: '800', color: '#14532d', letterSpacing: '-1px' },
  concepto: { color: '#374151', fontSize: '14px', marginTop: '4px' },
  cliente: { color: '#6b7280', fontSize: '13px' },
  secureBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '10px 14px',
    marginBottom: '20px'
  },
  secureLock: { fontSize: '14px' },
  secureText: { color: '#64748b', fontSize: '12px' },
  btn: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #0060aa, #0080cc)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '17px',
    fontWeight: '700',
    cursor: 'pointer',
    letterSpacing: '0.3px',
    boxShadow: '0 4px 14px rgba(0,96,170,0.35)',
    transition: 'opacity 0.15s'
  },
  redirigendoMsg: {
    color: '#6b7280',
    fontSize: '12px',
    textAlign: 'center',
    margin: '10px 0 0'
  },
  brandsRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    marginTop: '20px'
  },
  brandImg: { height: '24px', objectFit: 'contain' },
  openpayRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '12px'
  },
  openpayLabel: { color: '#9ca3af', fontSize: '11px' },
  openpayLogo: { height: '18px', objectFit: 'contain' },
  contacto: {
    color: '#9ca3af',
    fontSize: '11px',
    textAlign: 'center',
    marginTop: '16px',
    lineHeight: '1.7',
    borderTop: '1px solid #f3f4f6',
    paddingTop: '14px'
  },
  contactoLink: { color: '#0060aa', textDecoration: 'none' },
  spinner: {
    width: '36px', height: '36px',
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #0060aa',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 12px'
  },
  centered: { textAlign: 'center', color: '#6b7280' },
  title: { margin: '0 0 12px', color: '#111', fontSize: '22px' },
  errorIcon: {
    width: '56px', height: '56px',
    background: '#ef4444', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontSize: '22px', margin: '0 auto 16px'
  },
  errorMsg: {
    color: '#ef4444', fontSize: '13px', textAlign: 'center',
    margin: '0 0 12px',
    background: '#fef2f2', border: '1px solid #fecaca',
    borderRadius: '8px', padding: '10px 14px'
  }
};

export default PagarCheckout;
